import { useLocation, useNavigate } from 'react-router-dom'
import { useCallback, useMemo } from 'react'
import { ZodObject, z } from 'zod'

// types
import { SetZodQuery, UseZodQueryParamsOptions } from '../types/types'

/**
 * React hook for managing query parameters with Zod validation.
 * - Returns a query object (with only schema-defined keys) and a setter function.
 * - Setter can accept a full query object or an updater function.
 * - Unknown params are preserved in the URL, but not in the returned query object.
 */
const useZodQueryParams = <Schema extends ZodObject<any>>(
	zodSchema: Schema,
	options?: UseZodQueryParamsOptions
): [z.infer<Schema>, SetZodQuery<z.infer<Schema>>] => {
	const location = useLocation()
	const navigate = useNavigate()

	const schemaKeys = Object.keys(zodSchema.shape)

	const query = useMemo(() => {
		const searchParams = new URLSearchParams(location.search)
		const raw: Record<string, unknown> = {}

		Array.from(searchParams.entries()).forEach(([key, value]) => {
			if (raw[key] !== undefined) {
				raw[key] = Array.isArray(raw[key]) ? [...raw[key], value] : [raw[key], value]
			} else {
				raw[key] = value
			}
		})

		return zodSchema.parse(raw)
	}, [location.search, zodSchema])

	const setQuery: SetZodQuery<z.infer<Schema>> = useCallback(
		(newQueryOrUpdater, setOptions) => {
			const searchParams = new URLSearchParams(location.search)

			const currentQuery = query
			const newQuery = typeof newQueryOrUpdater === 'function' ? newQueryOrUpdater(currentQuery) : newQueryOrUpdater

			// Only update schema-defined keys, leave others untouched
			schemaKeys.forEach((key) => {
				if (key in newQuery) {
					const value = newQuery[key]
					searchParams.delete(key) // Remove existing to avoid duplicates

					if (value === null) {
						// Set as empty string in URL for null values
						searchParams.set(key, '')
					} else if (Array.isArray(value)) {
						// handle null/empty arrays
						if (value.length === 0) {
							searchParams.set(key, '')
						} else {
							value.forEach((item: unknown) => {
								if (item === null) {
									searchParams.append(key, '')
								} else if (item !== undefined) {
									searchParams.append(key, String(item))
								}
							})
						}
					} else if (value !== undefined) {
						searchParams.set(key, String(value))
					}
				}
			})

			// Unknown params are preserved in the URL
			// Use replace or push navigation based on options
			navigate({ search: searchParams.toString() }, { replace: setOptions?.replace ?? options?.replace ?? true })
		},
		[location.search, navigate, query, options, schemaKeys]
	)

	return [query, setQuery]
}

export default useZodQueryParams
